/**
 * This file aims to encapculate some helper functions in order to translate some of our data structures into valid geojson formats.
 * Please aim to keep functions relevant, and ensure they include `geoJSON` in the title.
 */

import {
  Geometry,
  GeoJsonProperties,
  Point,
  Feature,
  FeatureCollection,
} from 'geojson';

export const EMPTY_DATA_SOURCE = {
  type: 'FeatureCollection',
  features: [],
};

export const DEFAULT_GEOJSON_LAYER = {
  type: 'geojson',
  data: EMPTY_DATA_SOURCE,
};

export const createGeoJSONFeatureCollection = (
  features:
    | Array<Feature<Geometry>>
    | Array<Feature<Geometry, GeoJsonProperties>>
): FeatureCollection<Geometry> => {
  return {
    type: 'FeatureCollection',
    features,
  };
};

/**
 * Creates a geoJSON compatible data structure for the a feature.
 * The input requires a geoJSON compatible properties object AND a valid geometry object.
 * @param  {GeoJsonProperties} properties
 * @param  {Geometry} geometry
 * @returns Feature
 */
export const createGeoJSONFeature = (
  properties: GeoJsonProperties,
  geometry: Geometry
): Feature<Geometry, GeoJsonProperties> => {
  return {
    type: 'Feature',
    properties,
    geometry,
  };
};

/**
 * Creates a geoJSON compatible data structure for the geometry.
 * @param  {number[]} coordinates
 * @returns Point
 */
export const createGeoJSONGeometryPoint = (coordinates: number[]): Point => {
  return {
    type: 'Point',
    coordinates,
  };
};

/**
 * Creates a geoJSON compatible data structure for a point geometry as a feature.
 * @deprecated please use the individual {@link createGeoJSONGeometryPoint} and {@link createGeoJSONFeature} instead.
 * @param  {number} x
 * @param  {number} y
 * @param  {string} type
 */
export const createGeoJSONFeaturePoint = (
  x: number,
  y: number,
  type: string
): Feature => {
  const geometry = createGeoJSONGeometryPoint([x, y]);
  return createGeoJSONFeature({ type }, geometry);
};
